import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, Circle, Polygon, InfoBox } from "@react-google-maps/api";
import { GOOGLE_MAPS_KEY } from "../../../helpers/constants";
import { darkMode, lightMode } from "../mapStyles";
import { getCssVariableValue, getMapMarker } from "../../../helpers/helperFunctions";
import { useGeofenceContext } from "./GeofenceContext";
import { useLocalStorage } from "../../../helpers/customHooks";
// import {darkMode, lightMode} from "./mapStyles.js"
const containerStyle = {
	height: "100%",
	borderRadius: "12px",
};

const center = {
	lat: 59,
	lng: 18,
};

const GeofenceMap = ({ geofences }) => {
	const { creating, method, saveFence, setFenceData, initialData } = useGeofenceContext();
	const [polyPathData, setPolyPathData] = useState(null);
	const [circleData, setCircleData] = useState(null);
	const [center, setCenter] = useState(null)
	const [storage, setStorage] = useLocalStorage("mapsettings");
	const [mode, setMode] = useState(storage ? storage.maptype : "light");
	const [map, setMap] = useState(null);
	const editColor = getCssVariableValue("--green") ? getCssVariableValue("--green") : "#000000";
	const staticColor = getCssVariableValue("--light_navy") ? getCssVariableValue("--light_navy") : "#000000";
	const circleRef = useRef(null);
	const listenersCircleRef = useRef([]);
	const polygonRef = useRef(null);
	const listenersPolygonRef = useRef([]);
	const [visibleCircleGeo, setVisibleCircleGeo] = useState(null);
	const [visiblePolyGeo, setVisiblePolyGeo] = useState(null);
	const [userLocation, setUserLocation] = useState(null)

	const locationSuccess = (position) => {
		setUserLocation({lat: position.coords.latitude, lng: position.coords.longitude})
	}

	const locationFail = () => {
		setUserLocation({lat: 59, lng: 18})
	}	
	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: GOOGLE_MAPS_KEY,
	});
	const options = {
		minZoom: 1,
		restriction: {
			latLngBounds: {
				east: 179.9999,
				north: 85,
				south: -85,
				west: -179.9999,
			},
			strictBounds: true,
		},
		// styles: mode==="dark"?darkMode:lightMode,
		clickableIcons: false,
		mapTypeId: mode === "satellite" ? "satellite" : mode === "hybrid" ? "hybrid" : mode === "terrain" ? "terrain" : "roadmap",
		// mapId: "da381db21fff985f4e1232c0",
		mapTypeControl: false,
		streetViewControl: false,
		keyboardShortcuts: false,
		fullscreenControl: false,
		disableDefaultUI: true,
		tilt: 0,
		zoomControl: false,
		gestureHandling: "greedy",
		backgroundColor: "rgba(0,0,0,0)",
		styles: mode === "dark" ? darkMode : mode === "light" ? lightMode : null,
	};

	const onLoad = useCallback((map) => {
		setMap(map);
		navigator.geolocation.getCurrentPosition(locationSuccess, locationFail);
	}, []);

	const onUnmount = useCallback(() => {
		setMap(null);
	}, []);


	useEffect(() => {
		if (map) {
			//  map.setCenter({lat: props.device?.modelspecificinfo?.positioninfo?.latitude, lng: props.device?.modelspecificinfo?.positioninfo?.longitude})
			map.setZoom(11);
		}
	}, [map]);

	const onEditCircle = useCallback(() => {
		if (circleRef.current) {
			const center = circleRef.current.getCenter();
			const radius = circleRef.current.getRadius();
			setCircleData((prevCircleData) => {
				const hasCenterChanged = prevCircleData.center.lat !== center.lat() || prevCircleData.center.lng !== center.lng();
				const hasRadiusChanged = prevCircleData.radius !== radius;

				if (hasCenterChanged || hasRadiusChanged) {
					return {
						center: { lat: center.lat(), lng: center.lng() },
						radius: radius,
					};
				}
				return prevCircleData;
			});
		}
	}, [setCircleData]);

	// Bind refs to current Polygon and listeners
	const onLoadCircle = useCallback(
		(circle) => {
			circleRef.current = circle;
		},
		[onEditCircle]
	);

	// Clean up refs
	const onUnmountCircle = useCallback(() => {
		listenersCircleRef.current.forEach((lis) => lis.remove());
		circleRef.current = null;
	}, []);

	const creatingCircle = () => {
		return (
			<Circle
				options={{
					fillColor: editColor,
					fillOpacity: "0.5",
					strokeColor: editColor,
					strokeWeight: "2",
				}}
				editable
				//draggable
				center={circleData && circleData.center}
				radius={circleData && circleData.radius}
				onRadiusChanged={onEditCircle}
				onCenterChanged={onEditCircle}
				onMouseUp={onEditCircle}
				onDragEnd={onEditCircle}
				onLoad={onLoadCircle}
				onUnmount={onUnmountCircle}
			/>
		);
	};


	const onEditPolygon = useCallback(() => {
		if (polygonRef.current) {
			const nextPath = polygonRef.current
				.getPath()
				.getArray()
				.map((latLng) => {
					return { lat: latLng.lat(), lng: latLng.lng() };
				});
			setPolyPathData(nextPath);
		}
	}, [setPolyPathData]);

	// Bind refs to current Polygon and listeners
	const onLoadPoly = useCallback(
		(polygon) => {
			polygonRef.current = polygon;
			const path = polygon.getPath();
			listenersPolygonRef.current.push(path.addListener("set_at", onEditPolygon), path.addListener("insert_at", onEditPolygon), path.addListener("remove_at", onEditPolygon));
		},
		[onEditPolygon]
	);

	// Clean up refs
	const onUnmountPoly = useCallback(() => {
		listenersPolygonRef.current.forEach((lis) => lis.remove());
		polygonRef.current = null;
	}, []);

	const creatingPoly = () => {
		if(!polyPathData) return
		return (
			<Polygon
				options={{
					fillColor: editColor,
					fillOpacity: "0.5",
					strokeColor: editColor,
					strokeWeight: "2",
				}}
				editable
				draggable
				path={polyPathData}
				onMouseUp={onEditPolygon}
				onDragEnd={onEditPolygon}
				onLoad={onLoadPoly}
				onUnmount={onUnmountPoly}
			/>
		);
	};

	useEffect(() => {
		if (!map || !creating) return;
		const bounds = new window.google.maps.LatLngBounds();
		// initialize when no initial data
		if (!initialData) {
			const lat = userLocation?.lat || map.getCenter().lat();
			const lng = userLocation?.lng || map.getCenter().lng();
			if (method === "circle"){
				const radius = 1000;
				setCircleData({
					center: { lat, lng },
					radius,
				});
				const latLng = new window.google.maps.LatLng(lat, lng);
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
				return;
			}
			if(method === "poly"){
				let path = [
					{
						lat: lat,
						lng: lng,
					},
					{
						lat: lat - 0.01,
						lng: lng + 0.02,
					},
					{
						lat: lat - 0.01,
						lng: lng - 0.02,
					},
				];
				setPolyPathData(path);
				path.map((point) => {
					let lat = point.lat;
					let lng = point.lng;
					bounds.extend({
						lat: parseFloat(lat),
						lng: parseFloat(lng),
					});
					return bounds;
				});
				if (bounds && map) {
					map.fitBounds(bounds);
				}
				return
			}
			return
		}
		// initialize when you DO have initial data
		if (method === "circle") {
			if (!initialData.center) {
				const radius = 1000;
				const lat = parseFloat(initialData[0].latitude);
				const lng = parseFloat(initialData[0].longitude);
				setCircleData({ radius, center: { lat, lng } });
				const latLng = new window.google.maps.LatLng(lat, lng);
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
			} else {
				const radius = parseFloat(initialData.radius);
				const { lat, lng } = initialData.center;
				setCircleData({ radius, center: { lat, lng } });
				const latLng = new window.google.maps.LatLng(parseFloat(lat), parseFloat(lng));
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
			}
		}else{
			let path = [];
			if (initialData.center) {
				path = [
					{
						lat: initialData.center.lat,
						lng: initialData.center.lng,
					},
					{
						lat: initialData.center.lat - 0.01,
						lng: initialData.center.lng + 0.02,
					},
					{
						lat: initialData.center.lat - 0.01,
						lng: initialData.center.lng - 0.02,
					},
				];
			} else {
				path = initialData.map((location) => ({
					lat: location.latitude,
					lng: location.longitude,
				}));
			}
			setPolyPathData(path);
			// HERE
			path.map((point) => {
				let lat = point.lat;
				let lng = point.lng;
				bounds.extend({
					lat: parseFloat(lat),
					lng: parseFloat(lng),
				});
				return bounds;
			});
			if (bounds && map) {
				map.fitBounds(bounds);
			}
		}
	}, [map, creating, method, initialData]);


	useEffect(() => {
		if(!saveFence) return;
		setFenceData(method === "circle" ? circleData : polyPathData)
	}, [saveFence])


		const showGeofencesOnMap = () => {
		// if(creatingGeo){
		// 	return
		// }
		let circles = geofences?.filter((geofence) => geofence.type == "CIRCLE");
		let polys = geofences?.filter((geofence) => geofence.type == "POLYGON");
		return (
			<>
				{circles?.map((circle, index) => (
					<div key={index}>
						<Circle
							options={{
								fillColor: staticColor,
								fillOpacity: "0.5",
								strokeColor: staticColor,
								strokeWeight: "2",
								clickable: false,
							}}
							center={{ lat: circle.latitude, lng: circle.longitude }}
							radius={parseFloat(circle.radius)}
							onMouseOut={() => setVisibleCircleGeo(null)}
							onMouseOver={() => setVisibleCircleGeo(index)}
						/>
						{(visibleCircleGeo === index) && (
							<InfoBox
								position={{ lat: circle.latitude, lng: circle.longitude }}
								options={{
									disableAutoPan: true,
									closeBoxURL: "",
									boxStyle: {
										width: "auto",
										boxShadow: "0 3px 18px -8px rgba(0,0,0,0.15)",
										borderRadius: "5px",
									},
									pixelOffset: new window.google.maps.Size(-0, -0),
								}}
							>
								<div className="bg-black p-1 d-flex align-items-center">
									<span className="text-white">{circle.name}</span>
								</div>
							</InfoBox>
						)}
					</div>
				))}
				{polys?.map((poly, index) => {
					let centroid = poly.points.reduce(
						(acc, point) => {
							acc.lat += point.latitude;
							acc.lng += point.longitude;
							return acc;
						},
						{ lat: 0, lng: 0 }
					);

					centroid.lat /= poly.points.length;
					centroid.lng /= poly.points.length;
					return (
						<div key={index}>
							<Polygon
								options={{
									fillColor: staticColor,
									fillOpacity: "0.5",
									strokeColor: staticColor,
									strokeWeight: "2",
									clickable: false,
								}}
								paths={poly.points?.map((point) => ({
									lat: point.latitude,
									lng: point.longitude,
								}))}
								onMouseOut={() => setVisiblePolyGeo(null)}
								onMouseOver={() => setVisiblePolyGeo(index)}
							/>
							{(visiblePolyGeo === index) && (
								<InfoBox
									position={{ lat: centroid.lat, lng: centroid.lng }}
									options={{
										disableAutoPan: true,
										closeBoxURL: "",
										boxStyle: {
											width: "auto",
											boxShadow: "0 3px 18px -8px rgba(0,0,0,0.15)",
											borderRadius: "5px",
										},
										pixelOffset: new window.google.maps.Size(-25, -6),
									}}
								>
									<div className="bg-black p-1 d-flex align-items-center">
										<span className="text-white">{poly.name}</span>
									</div>
								</InfoBox>
							)}
						</div>
					);
				})}
			</>
		);
	};

	return isLoaded ? (
		<div className="w-100 h-100">
			<GoogleMap mapContainerStyle={containerStyle} center={center} zoom={11} onLoad={onLoad} onUnmount={onUnmount} options={options}>
				{creating && method === "circle" ? creatingCircle() : null}
				{creating && method === "poly" ? creatingPoly() : null}
				{showGeofencesOnMap()}
			</GoogleMap>
		</div>
	) : (
		<></>
	);
};

export default GeofenceMap;